import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import cx from "classnames";
import Popover from "@material-ui/core/Popover";
import Checkbox from "@material-ui/core/Checkbox";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import Add from "@material-ui/icons/Add";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import { useTranslation } from "react-i18next";
import { useFeedback } from "./FeedbackContext";
import {
  CATEGORY_OPTIONS,
  ALL_DISPLAY_STATUSES,
  getDisplayStatus,
} from "./feedbackConstants";
import { topicListStyles, STATUS_COLORS } from "./feedbackStyles";
import FeedbackTopicForm from "./FeedbackTopicForm";

const useStyles = makeStyles(topicListStyles);

function formatDate(iso) {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${mon}`;
}

function FilterPopover({ label, items, selected, onToggle, colorMap }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const activeCount = selected.length;

  return (
    <>
      <button
        className={cx(classes.filterBtn, {
          [classes.filterBtnActive]: activeCount > 0,
        })}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        {label}
        {activeCount > 0 && ` (${activeCount})`}
        <ArrowDropDown style={{ fontSize: "16px", marginRight: "-4px" }} />
      </button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ style: { marginTop: 4 } }}
      >
        <div className={classes.popoverContent}>
          {items.map((item) => {
            const checked = selected.includes(item.value);
            const color = colorMap?.[item.value];
            return (
              <div
                key={item.value}
                className={classes.popoverItem}
                onClick={() => onToggle(item.value)}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  className={classes.popoverCheckbox}
                  style={
                    checked
                      ? { color: color ? color.text : "#555555" }
                      : undefined
                  }
                  disableRipple
                />
                {color && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: color.text,
                      flexShrink: 0,
                    }}
                  />
                )}
                {t(item.labelKey)}
              </div>
            );
          })}
        </div>
      </Popover>
    </>
  );
}

FilterPopover.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  colorMap: PropTypes.object,
};

export default function FeedbackTopicList({ clientColor }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    filteredTopics,
    selectedTopicId,
    setSelectedTopicId,
    filterStatuses,
    toggleFilterStatus,
    filterCategories,
    toggleFilterCategory,
    creatingTopic,
    setCreatingTopic,
    pendingAiTitleIds,
  } = useFeedback();

  const btnBg = clientColor || "#555";

  return (
    <>
      <div className={classes.listHeader}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#444" }}>
          {t("feedback.topics")}
        </span>
        <button
          className={classes.addButton}
          style={{
            background: btnBg,
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "3px",
          }}
          onClick={() => setCreatingTopic(true)}
        >
          <Add style={{ fontSize: "14px" }} /> {t("feedback.new")}
        </button>
      </div>

      {creatingTopic && <FeedbackTopicForm clientColor={clientColor} />}

      <div className={classes.filters}>
        <FilterPopover
          label={t("feedback.status_filter")}
          items={ALL_DISPLAY_STATUSES}
          selected={filterStatuses}
          onToggle={toggleFilterStatus}
          colorMap={STATUS_COLORS}
        />
        <FilterPopover
          label={t("feedback.category_filter")}
          items={CATEGORY_OPTIONS}
          selected={filterCategories}
          onToggle={toggleFilterCategory}
        />
      </div>

      <div className={classes.topicsList}>
        {filteredTopics.length === 0 && (
          <div className={classes.noResults}>
            {t("feedback.no_topics_found")}
          </div>
        )}

        {filteredTopics.map((topic) => {
          const displayStatus = getDisplayStatus(topic);
          const statusColor =
            STATUS_COLORS[displayStatus] || STATUS_COLORS.open;
          const isSelected = topic.id === selectedTopicId;
          const isPendingTitle = pendingAiTitleIds.has(topic.id);

          return (
            <div
              key={topic.id}
              className={cx(classes.topicCard, {
                [classes.topicCardSelected]: isSelected,
              })}
              onClick={() => {
                setSelectedTopicId(topic.id);
                setCreatingTopic(false);
              }}
            >
              <div
                className={classes.topicTitle}
                style={
                  isPendingTitle
                    ? { fontStyle: "italic", color: "#999" }
                    : undefined
                }
              >
                {isPendingTitle
                  ? t("feedback.defining_topic_name")
                  : topic.title}
              </div>
              <div className={classes.topicMeta}>
                <span
                  className={classes.chip}
                  style={{
                    backgroundColor: statusColor.bg,
                    color: statusColor.text,
                  }}
                >
                  {t(`feedback.status_${displayStatus}`)}
                </span>
                {topic.category && (
                  <span
                    className={classes.chip}
                    style={{ backgroundColor: "#f0f0f0", color: "#666" }}
                  >
                    {t(`feedback.cat_${topic.category}`)}
                  </span>
                )}
              </div>
              <div className={classes.topicFooter}>
                <span>
                  {topic.created_by_name} · {formatDate(topic.updated_at)}
                </span>
                <span className={classes.commentCount}>
                  <ChatBubbleOutline style={{ fontSize: "11px" }} />
                  {topic.comment_count || 0}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

FeedbackTopicList.propTypes = {
  clientColor: PropTypes.string,
};
